// src/pages/account/Subscription.js
import React, { useState, useEffect, useContext, useCallback, useRef, Suspense, lazy } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import './Subscription.css';
import { validateName, validatePassword, validateEmail } from '../../utils/userValidation';
import { useNavigate, Link } from 'react-router-dom';
import { BASE_URL, IS_PRERENDERING } from '../../utils/config';
import { NotificationContext } from '../../context/NotificationContext'; // Import NotificationContext
import { apiFetch } from '../../utils/apiClient'; // Import apiFetch
import { useRecaptcha } from '../../hooks/useRecaptcha'; // Import the hook
import { useLanguage } from '../../hooks/useLanguage';

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

const Subscription = () => {
  const { t } = useTranslation();
  const lang = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    email: '',
    ageConfirmed: false, // Default to not confirmed
    preferences: {
      subscribe: false, // Default to not subscribed
      uiLanguage: lang, // Set the current language
    },
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext); // Get addNotification
  const handleVerifyRef = useRef(null); // Create a ref to hold the callback
  const [error, setError] = useState(null);

  // If an error was set from an async callback, throw it during render to be caught by the Error Boundary
  if (error) {
    throw error;
  }

  // New useEffect for real-time form validation
  useEffect(() => {
    const { firstName, lastName, password, email, ageConfirmed } = formData;
    // Check if all mandatory fields are filled
    const allRequiredFieldsFilled = firstName && lastName && password && email;

    // Check if there are any errors currently stored in the state.
    const noValidationErrors = Object.values(formErrors).every((error) => !error);
    setIsFormValid(allRequiredFieldsFilled && noValidationErrors && agreedToTerms && ageConfirmed);
  }, [formData, agreedToTerms, formErrors]); // Now depends on formErrors state

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = null;
    switch (name) {
      case 'firstName':
      case 'lastName':
        error = validateName(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      default:
        break;
    }
    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'preferences.subscribe') {
      setFormData((prev) => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          subscribe: value === 'yes', // Radio button value is 'yes' or 'no'
        },
      }));
    } else if (name === 'ageConfirmed') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'agreedToTerms') {
      setAgreedToTerms(checked);
    } else {
      setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nameError = validateName(formData.firstName) || validateName(formData.lastName);
    const passwordError = validatePassword(formData.password);
    const emailError = validateEmail(formData.email);

    setFormErrors({
      firstName: validateName(formData.firstName),
      lastName: validateName(formData.lastName),
      password: passwordError,
      email: emailError,
    });

    if (nameError || passwordError || emailError) {
      return;
    }

    if (!formData.ageConfirmed) {
      addNotification(t('subscriptionPage.ageConfirmationWarning'), 'warning');
      return;
    }

    if (!agreedToTerms) {
      addNotification(t('subscriptionPage.termsWarning'), 'warning');
      return;
    }

    if (IS_PRERENDERING) {
      addNotification('Form submission is disabled during page generation.', 'info');
      return;
    }

    setIsSubmitting(true);
    addNotification(t('subscriptionPage.submitting'), 'info');
    execute();
  };

  const handleRecaptchaExpire = useCallback(() => {
    //    console.log('[Subscription] reCAPTCHA expired or was closed by the user.');
    setIsSubmitting(false);
    addNotification(t('recaptcha.expiredReload'), 'warning');
  }, [addNotification, t]);

  const handleRecaptchaError = useCallback(
    (err) => {
      console.error('[Subscription] reCAPTCHA failed to load. Resetting form state.', err);
      setIsSubmitting(false);
      addNotification(t('recaptcha.loadErrorReload'), 'error', 7000);
      setError(new Error(t('recaptcha.loadErrorReload'))); // Propagate error to be caught by Error Boundary
    },
    [addNotification, t, setError]
  );

  // We pass a stable wrapper function to useRecaptcha.
  // This wrapper will call the latest version of our handler from the ref.
  const { recaptchaRef, execute, onChange, reset, onErrored, onExpired } = useRecaptcha((token) => handleVerifyRef.current(token), handleRecaptchaExpire, handleRecaptchaError);

  const handleReset = useCallback(() => {
    setFormData({
      firstName: '',
      lastName: '',
      password: '',
      email: '',
      ageConfirmed: false,
      preferences: {
        subscribe: false, // Default to not subscribed on reset
        uiLanguage: lang, // Keep the current language
      },
    });
    setFormErrors({});
    setAgreedToTerms(false);
  }, [lang]);

  const handleVerify = useCallback(
    async (recaptchaToken) => {
      try {
        const body = { ...formData, recaptchaToken };
        const response = await apiFetch(
          `${BASE_URL}/authUser/register`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
          null // No token needed for registration
        );

        if (response.ok) {
          const registrationData = await response.json();
          //          console.log('Subscription successful:', registrationData, formData);

          // --- Request email verification (fire and forget) ---
          const emailForVerification = registrationData?.user?.email || formData.email;
          if (emailForVerification) {
            // We don't await this or handle errors here. The success page will provide a resend option.
            apiFetch(`${BASE_URL}/authUser/request-verification-email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: emailForVerification }),
            }).catch((err) => {
              console.error('Initial verification email failed to send in the background:', err);
            });
          }
          // --- End request email verification ---

          handleReset();
          navigate(`/${lang}/subscription-success`, { state: { email: formData.email, bonusAwarded: registrationData.bonusAwarded } }); // Navigate to success page
        } else {
          console.log('[Subscription] Server returned an error. Resetting reCAPTCHA.');
          reset(); // Reset reCAPTCHA to allow another submission attempt.
          try {
            const errorData = await response.json();
            console.error('Subscription failed:', errorData);
            // A 409 Conflict status specifically indicates a duplicate resource (like an email).
            if (response.status === 409) {
              const handleGoToLogin = () => {
                navigate(`/${lang}/login`);
              };

              addNotification(
                t('subscriptionPage.emailExistsError.message'),
                'warning', // Use 'warning' type for this kind of info
                0, // duration 0 makes it persistent until closed
                true, // isPersistent
                [
                  { text: t('subscriptionPage.emailExistsError.goToLogin'), onClick: handleGoToLogin },
                  { text: t('subscriptionPage.emailExistsError.close'), onClick: () => {} },
                ]
              );
              setFormErrors((prevErrors) => ({
                ...prevErrors,
                email: t('subscriptionPage.emailExistsError.fieldError'),
              }));
            } else {
              addNotification(t('subscriptionPage.genericError', { error: errorData.error || t('subscriptionPage.serverError'), message: errorData.message || '', details: errorData.details || '' }), 'error');
            }
          } catch (jsonError) {
            const errorText = await response.text();
            console.error('Subscription failed (non-JSON response):', errorText);
            addNotification(t('subscriptionPage.genericError', { error: t('subscriptionPage.serverError'), message: '', details: '' }), 'error');
          }
        }
      } catch (error) {
        reset(); // Also reset on network errors.
        setError(error);
      } finally {
        setIsSubmitting(false);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData, navigate, addNotification, reset, setError, t, lang, handleReset]
  );

  // Keep the ref updated with the latest version of the handler.
  useEffect(() => {
    handleVerifyRef.current = handleVerify;
  }, [handleVerify]);

  return (
    <div className="subscription-page-container">
      <div className="login-container">
        <h5>{t('subscriptionPage.title')}</h5>
        <form onSubmit={handleSubmit}>
          {/* email */}
          <div className="form-group">
            <label htmlFor="email">{t('subscriptionPage.emailLabel')}</label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur} required autoComplete="email" />
            {formErrors.email && <div className="error-message">{formErrors.email}</div>}
          </div>

          {/* password */}
          <div className="form-group">
            <label htmlFor="password">{t('subscriptionPage.passwordLabel')}</label>
            <div className="password-input-wrapper">
              <input type={showPassword ? 'text' : 'password'} id="password" name="password" value={formData.password} onChange={handleChange} onBlur={handleBlur} required autoComplete="new-password" />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('subscriptionPage.hidePassword') : t('subscriptionPage.showPassword')}
                title={showPassword ? t('subscriptionPage.hidePassword') : t('subscriptionPage.showPassword')}
              >
                <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </button>
            </div>
            {formErrors.password && <div className="error-message">{formErrors.password}</div>}
          </div>
          {/* First Name and Last Name in a row */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('subscriptionPage.firstNameLabel')}</label>
              <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} onBlur={handleBlur} required autoComplete="given-name" />
              {formErrors.firstName && <div className="error-message">{formErrors.firstName}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="lastName">{t('subscriptionPage.lastNameLabel')}</label>
              <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} onBlur={handleBlur} required autoComplete="family-name" />
              {formErrors.lastName && <div className="error-message">{formErrors.lastName}</div>}
            </div>
          </div>

          {/* Age Confirmation Checkbox */}
          <div className="form-group terms-group">
            <input type="checkbox" id="ageConfirmed" name="ageConfirmed" checked={formData.ageConfirmed} onChange={handleChange} />
            <label htmlFor="ageConfirmed" className="terms-label">
              {t('subscriptionPage.ageConfirmationLabel')}
            </label>
          </div>

          {/* subscribe */}
          <div className="form-group">
            <label htmlFor="subscribe">{t('subscriptionPage.subscribeLabel')}</label>
            <div className="radio-container">
              <input type="radio" id="subscribeYes" name="preferences.subscribe" value="yes" checked={formData.preferences.subscribe === true} onChange={handleChange} />
              <label htmlFor="subscribeYes">{t('subscriptionPage.subscribeYes')}</label>
              <input type="radio" id="subscribeNo" name="preferences.subscribe" value="no" checked={formData.preferences.subscribe === false} onChange={handleChange} />
              <label htmlFor="subscribeNo">{t('subscriptionPage.subscribeNo')}</label>
            </div>
            <small className="form-text text-muted">{t('subscriptionPage.subscribeDescription')}</small>
          </div>

          {/* Terms and Conditions Checkbox */}
          <div className="form-group terms-group">
            <input type="checkbox" id="agreeTerms" name="agreeTerms" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
            <label htmlFor="agreeTerms" className="terms-label">
              <Trans
                i18nKey="subscriptionPage.termsLabel"
                components={{
                  termsLink: <Link to={`/${lang}/terms-of-use`} target="_blank" rel="noopener noreferrer" />,
                  privacyLink: <Link to={`/${lang}/privacy-policy`} target="_blank" rel="noopener noreferrer" />,
                }}
              >
                I have read and agree to the {'<termsLink>'}Terms of Use{'</termsLink>'} and {'<privacyLink>'}Privacy Policy{'</privacyLink>'}.
              </Trans>
            </label>
          </div>

          <div className="button-group">
            <button type="button" className="cancel-btn" onClick={handleReset} disabled={isSubmitting}>
              {t('subscriptionPage.cancelButton')}
            </button>
            <button type="submit" className="subscribe-btn" disabled={!isFormValid || isSubmitting}>
              {t('subscriptionPage.subscribeButton')}
            </button>
          </div>
        </form>
        {!IS_PRERENDERING && (
          <Suspense fallback={null}>
            <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={onChange} onErrored={onErrored} onExpired={onExpired} />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default Subscription;
